import {
  BellIcon,
  BriefcaseIcon,
  BuildingsIcon,
  ChartBarIcon,
  ChartLineIcon,
  ClipboardTextIcon,
  EngineIcon,
  FilesIcon,
  MagnifyingGlassIcon,
  NotePencilIcon,
  SealCheckIcon,
  ShieldCheckIcon,
  // SlidersHorizontalIcon, — restore with the hidden Notification Settings nav item
  SquaresFourIcon,
  StackIcon,
  // StethoscopeIcon, — restore with the hidden Diagnostics nav item
  TargetIcon,
  UsersThreeIcon,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

/**
 * Static nav-item registry for the Sidebar.
 *
 * Items are organised into `NAV_GROUPS` — the sidebar renders a small section
 * heading per group (and a divider when collapsed). The flat `NAV_ITEMS` export
 * is kept so `findNavItemByPath` (used by Breadcrumbs) and any external
 * consumers keep working.
 *
 * `permission` is the module-level gate from
 * `Guides/ROLE_USER_MANAGEMENT_GUIDE.md` §18 — Sidebar's `filterNavByPermissions`
 * accepts items whose `permission` is a prefix of any held permission with a
 * `.` separator (so e.g. `logsheet.maker` matches `logsheet.maker.create`).
 *
 * `role` (Phase 23 D-243) is an exact-match role gate applied after permissions —
 * used by the Super-Admin-only Diagnostics entry.
 */
export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: ComponentType<{ size?: number; weight?: 'regular' | 'duotone' | 'fill' | 'bold' }>;
  readonly permission?: string;
  readonly role?: string;
  /** Optional test id suffix used to attach data-testid to the nav link. */
  readonly testId?: string;
}

export interface NavGroup {
  /** Section heading shown when the sidebar is expanded; replaced with a
   *  divider line when the sidebar is collapsed to icon-only. */
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: '/dashboard', icon: SquaresFourIcon },
      {
        label: 'Alerts',
        to: '/alerts',
        icon: BellIcon,
        permission: 'notifications.view',
        testId: 'sidebar-nav-alerts',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'My Logsheets',
        to: '/logsheets/maker',
        icon: NotePencilIcon,
        permission: 'logsheet.maker',
      },
      {
        label: 'Checker Queue',
        to: '/logsheets/checker/pending',
        icon: MagnifyingGlassIcon,
        permission: 'logsheet.checker.review',
      },
      {
        label: 'Approver Queue',
        to: '/logsheets/approver/pending',
        icon: SealCheckIcon,
        permission: 'logsheet.approver.review',
        testId: 'sidebar-nav-approver-queue',
      },
      {
        label: 'Work Orders',
        to: '/work-orders',
        icon: ClipboardTextIcon,
        permission: 'workorders.view',
        testId: 'sidebar-nav-work-orders',
      },
      {
        label: 'Monthly Documents',
        to: '/logsheets/monthly-documents/upload',
        icon: FilesIcon,
        permission: 'logsheet.monthly.upload-documents',
        testId: 'sidebar-nav-monthly-documents',
      },
    ],
  },
  {
    label: 'Reports',
    items: [
      {
        label: 'Monthly Summary',
        to: '/logsheets/monthly-summary',
        icon: ChartLineIcon,
        permission: 'logsheet.monthly.read',
        testId: 'sidebar-nav-monthly-summary',
      },
      // Phase 25 Plan 25-04 — Logsheet Report multi-lens surface.
      // Gated by `reports.logsheet.view`; only visible to holders.
      {
        label: 'Logsheet Report',
        to: '/reports/logsheet',
        icon: ChartBarIcon,
        permission: 'reports.logsheet.view',
        testId: 'sidebar-nav-logsheet-report',
      },
    ],
  },
  {
    label: 'Master Data',
    items: [
      { label: 'Sites', to: '/sites', icon: BuildingsIcon, permission: 'sites' },
      {
        label: 'Clients',
        to: '/clients',
        icon: BriefcaseIcon,
        permission: 'clients.view',
        testId: 'sidebar-nav-clients',
      },
      { label: 'Machine Types', to: '/machine-types', icon: StackIcon, permission: 'machineTypes' },
      { label: 'Machines', to: '/machines', icon: EngineIcon, permission: 'machines' },
      {
        label: 'KPI Templates',
        to: '/kpi-templates',
        icon: TargetIcon,
        permission: 'kpis.manage_templates',
        testId: 'sidebar-nav-kpi-templates',
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      { label: 'Users', to: '/users', icon: UsersThreeIcon, permission: 'users' },
      { label: 'Roles', to: '/roles', icon: ShieldCheckIcon, permission: 'roles' },
      // TEMPORARILY HIDDEN (2026-06-15): Notification Settings and Diagnostics
      // are hidden from the sidebar for ALL roles, including Super Admin. The
      // routes (`/notification-settings`, `/diagnostics`) remain wired in
      // `routes/index.tsx` and are still reachable by direct URL — only the
      // sidebar entries are suppressed. To restore, un-comment the two items
      // below and re-add the `SlidersHorizontalIcon` / `StethoscopeIcon`
      // imports at the top of this file, then redeploy.
      // {
      //   label: 'Notification Settings',
      //   to: '/notification-settings',
      //   icon: SlidersHorizontalIcon,
      //   permission: 'notifications.manage_settings',
      //   testId: 'sidebar-nav-notification-settings',
      // },
      // {
      //   label: 'Diagnostics',
      //   to: '/diagnostics',
      //   icon: StethoscopeIcon,
      //   role: 'Super Admin',
      //   testId: 'sidebar-nav-diagnostics',
      // },
    ],
  },
] as const;

/** Flat list of every nav item across all groups — preserves backward compatibility
 *  for `findNavItemByPath` (Breadcrumbs) and any external import sites. */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

// REVIEW FP-007: precompute the longest-path-first sort once at module scope
// so Breadcrumbs doesn't clone + sort the array on every route change.
const NAV_ITEMS_BY_PATH_DESC: readonly NavItem[] = [...NAV_ITEMS].sort(
  (a, b) => b.to.length - a.to.length,
);

/** Helper for breadcrumbs: returns the matching nav-item label for a given pathname (best-effort). */
export function findNavItemByPath(pathname: string): NavItem | undefined {
  return NAV_ITEMS_BY_PATH_DESC.find(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}
